import React, {
	useState,
	useRef,
	useCallback,
	useEffect,
	type KeyboardEvent,
	memo,
	useImperativeHandle,
	type PropsWithChildren,
	useMemo,
} from 'react';
import noop from 'lodash/noop';
import Avatar from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import type { TriggerProps } from '@atlaskit/popup';
import { Flex, Box, xcss, Pressable } from '@atlaskit/primitives';
import type { UserOption } from '@atlassian/jira-base-user-picker/src/types.tsx';
import { CellWrapper } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { PopupComponent } from '@atlassian/jira-business-fields/src/common/ui/popup/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { DEFAULT_ROW_HEIGHT } from '../../../common/constants.tsx';
import { AvatarItem, DisplayName } from '../../../common/ui/list-cell/styled.tsx';
import UserPicker from './async-user-picker/index.tsx';
import messages from './messages.tsx';
import type { MultiUserCellProps } from './types.tsx';
import { isUserOptionsEqual, transformToUserOption } from './utils.tsx';

const MENU_MIN_WIDTH = 260;
const MAX_AVATAR = 5;

const POPUP_OFFSET_NEW: [number, number] = [0, 6 - DEFAULT_ROW_HEIGHT];

type MultiUserReadViewProps = {
	isEditable: boolean;
	userOptions: UserOption[] | null;
};

export const MultiUserReadView = memo<MultiUserReadViewProps>(
	({ isEditable, userOptions }: MultiUserReadViewProps) => {
		const { formatMessage } = useIntl();

		const content = (
			<>
				{userOptions && userOptions.length > 0 ? (
					<AvatarGroup
						appearance="stack"
						data={userOptions.map((user) => ({
							name: user.name,
							src: user.avatarUrl ?? '',
						}))}
						maxCount={MAX_AVATAR}
						size="small"
					/>
				) : (
					<AvatarItem isUnassigned>
						<Avatar appearance="circle" src="" size="small" />
						<DisplayName>{formatMessage(messages.emptyMultiUser)}</DisplayName>
					</AvatarItem>
				)}
			</>
		);

		return (
			<AvatarGroupContainer
				isEditable={isEditable}
				testId="business-list.ui.list-view.multi-user-cell.avatar-group-container"
			>
				{content}
			</AvatarGroupContainer>
		);
	},
);

const MultiUserCell = memo<MultiUserCellProps>(
	({
		initialValue: _initialValue,
		fieldKey,
		fieldType,
		issueKey,
		isEditable,
		width,
		height,
		isSelected = false,
		wrapperRef,
		fieldName = '',
		issueType = '',
		issueTypeId = '',
		editMode = false,
		setEditMode = noop,
		isHovering = false,
		cellRef,
	}: MultiUserCellProps) => {
		const initialValue = useMemo(
			() => _initialValue?.map(transformToUserOption).filter(Boolean) ?? null,
			[_initialValue],
		);

		const userPickerWidth = width > MENU_MIN_WIDTH ? width : MENU_MIN_WIDTH;
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const [{ value: savedValue, error }, { saveValue }] = useEditField<UserOption[] | null, null>({
			fieldKey,
			issueKey,
			fieldType,
			initialValue,
			onFailure: () => {
				setUserOptions(savedValue ?? null);
			},
		});

		const [userOptions, setUserOptions] = useState<UserOption[] | null>(savedValue);
		const wasEditMode = usePreviousWithInitial(editMode);
		const hasValueUpdated = useRef(false);

		// Update the internal state in response to external data changes. Note
		// that setting state here is more efficient than with a `useEffect`
		// hook - see
		// https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
		if (!editMode && !isUserOptionsEqual(savedValue, userOptions) && !wasEditMode) {
			setUserOptions(savedValue);
		}

		const handleChange = useCallback((userPickerValue: UserOption) => {
			let newValue: UserOption[] | null = null;
			if (userPickerValue) {
				newValue = Array.isArray(userPickerValue) ? userPickerValue : null;
			}
			setUserOptions(newValue);

			/**
			 * `hasValueUpdated` is used in keyboard navigation to check whether pressing Enter has changed the value,
			 * so that we know if we need to exit edit mode on `Enter` or not
			 */
			hasValueUpdated.current = true;

			setTimeout(() => {
				hasValueUpdated.current = false;
			});
		}, []);

		const saveAndClose = useCallback(() => {
			setEditMode(false);

			if (isUserOptionsEqual(savedValue, userOptions)) {
				return;
			}

			saveValue(userOptions, null, createAnalyticsEvent({}));
		}, [createAnalyticsEvent, saveValue, savedValue, setEditMode, userOptions]);

		const handleContentKeyDown = useCallback(
			(e: KeyboardEvent<Element>) => {
				if (e.key === 'Escape') {
					e.preventDefault();
					saveAndClose();
					wrapperRef?.current?.focus();
				}
			},
			[saveAndClose, wrapperRef],
		);

		useEffect(() => {
			if (wasEditMode && !editMode) {
				saveAndClose();
			}
		}, [editMode, saveAndClose, wasEditMode]);

		const handleKeyDownNew = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (editMode && event.code === 'Enter') {
					if (!hasValueUpdated.current) {
						setEditMode(false);
						return false;
					}

					return true;
				}

				return false;
			},
			[editMode, setEditMode],
		);

		const cellOverrides = useCallback(
			() => ({
				onKeyDown: handleKeyDownNew,
			}),
			[handleKeyDownNew],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		const popupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<Pressable
					{...triggerProps}
					xcss={popupTriggerStyles}
					testId="business-list.ui.list-view.multi-user-cell.popup-trigger"
				>
					<MultiUserReadView isEditable={isEditable} userOptions={userOptions} />
				</Pressable>
			),
			[isEditable, userOptions],
		);

		const popupContent = useCallback(
			() => (
				<UserPicker
					width={width}
					height={height}
					userPickerWidth={userPickerWidth}
					menuMinWidth={MENU_MIN_WIDTH}
					placeholder={formatMessage(messages.placeholder)}
					fieldId={fieldType}
					value={userOptions}
					// @ts-expect-error - TS2322 - Type '(userPickerValue: UserOption) => void' is not assignable to type '() => void'.
					onChange={handleChange}
					onKeyDown={handleContentKeyDown}
					autoCompleteUrl={`/rest/api/3/user/recommend?${new URLSearchParams({
						context: 'CustomMultiUserField',
						...(issueKey && { issueKey }),
						query: '',
					})}`}
				/>
			),
			[
				issueKey,
				fieldType,
				formatMessage,
				handleChange,
				handleContentKeyDown,
				height,
				userOptions,
				userPickerWidth,
				width,
			],
		);

		const content =
			isHovering || editMode || isSelected ? (
				<Popup
					offset={POPUP_OFFSET_NEW}
					placement="bottom-start"
					messageId="business-list.ui.list-view.multi-user-cell.popup"
					messageType="transactional"
					isOpen={editMode}
					onClose={saveAndClose}
					popupComponent={PopupComponent}
					autoFocus={false}
					trigger={popupTrigger}
					content={popupContent}
				/>
			) : (
				<MultiUserReadView isEditable={isEditable} userOptions={userOptions} />
			);

		const cellContent = (
			<>
				{isEditable ? (
					content
				) : (
					<MultiUserReadView isEditable={isEditable} userOptions={userOptions} />
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		return (
			<CellWrapper
				isEditable={isEditable}
				testId="business-list.ui.list-view.multi-user-cell.cell-container"
				hasPadding={false}
			>
				<ContentWrapper>{cellContent}</ContentWrapper>
			</CellWrapper>
		);
	},
);

export default MultiUserCell;

const ContentWrapper = ({ children }: PropsWithChildren) => (
	<Box xcss={[wrapperStyles]}>{children}</Box>
);

const AvatarGroupContainer = ({
	children,
	isEditable,
	testId,
}: PropsWithChildren & { isEditable: boolean; testId: string }) => (
	<Flex
		testId={testId}
		alignItems="center"
		xcss={[
			avatarGroupContainerStyles,
			isEditable ? avatarGroupContainerPointerStyles : avatarGroupContainerDefaultStyles,
		]}
	>
		{children}
	</Flex>
);

const avatarGroupContainerStyles = xcss({
	paddingLeft: 'space.100',
	width: '100%',
});

const avatarGroupContainerDefaultStyles = xcss({
	cursor: 'default',
});

const avatarGroupContainerPointerStyles = xcss({
	cursor: 'pointer',
});

const wrapperStyles = xcss({
	width: '100%',
});

const popupTriggerStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
});
